import cn from 'classnames';
import { MouseEventHandler } from 'react';

import { Icon } from '../Icon';

import styles from './Button.module.scss';

type BaseProps = {
  type?:
    | 'primary'
    | 'secondary'
    | 'colored'
    | 'default'
    | 'outlined'
    | 'text'
    | 'inline-text';
  size?: 'l' | 'm' | 's' | 'xs';
  wide?: boolean;
  as?: 'button' | 'div' | 'a';
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  children?: React.ReactNode;
  onClick?: MouseEventHandler | (() => void | Promise<void>);
  /**
   * @deprecated Use {@link BaseProps.leftIcon} or {@link BaseProps.rightIcon} instead.
   */
  icon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
};
type AsLinkProps = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
> &
  BaseProps;

export type ButtonProps<T extends BaseProps['as']> = T extends 'a'
  ? AsLinkProps
  : BaseProps;

export function Button({
  type = 'primary',
  size = 'm',
  wide = false,
  as = 'button',
  disabled = false,
  isLoading = false,
  className,
  children,
  onClick,
  icon,
  leftIcon,
  rightIcon,
  ...otherProps
}: ButtonProps<'a' | 'button'>): JSX.Element {
  const ButtonElement = as;

  if (as === 'a') {
    return (
      <a
        className={cn(
          styles.button,
          styles[type],
          {
            [styles.large]: size === 'l',
            [styles.small]: size === 's',
            [styles.xSmall]: size === 'xs',
            [styles.wide]: wide,
            [styles.isLoading]: isLoading,
          },
          className,
        )}
        type="button"
        aria-disabled={disabled}
        {...otherProps}
      >
        {isLoading && (
          <Icon
            icon="isLoading"
            collection="ui"
            className={styles.loadingIcon}
          />
        )}
        {leftIcon !== undefined && leftIcon}
        {children}
        {icon !== undefined && icon}
        {rightIcon !== undefined && rightIcon}
      </a>
    );
  }

  return (
    <ButtonElement
      type="button"
      className={cn(
        styles.button,
        styles[type],
        {
          [styles.large]: size === 'l',
          [styles.small]: size === 's',
          [styles.xSmall]: size === 'xs',
          [styles.wide]: wide,
          [styles.isLoading]: isLoading,
        },
        className,
      )}
      disabled={disabled}
      onClick={onClick}
      {...otherProps}
    >
      {isLoading && (
        <Icon icon="isLoading" collection="ui" className={styles.loadingIcon} />
      )}
      {leftIcon !== undefined && leftIcon}
      {children}
      {icon !== undefined && icon}
      {rightIcon !== undefined && rightIcon}
    </ButtonElement>
  );
}
